import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@material-ui/icons/Close";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  Link,
} from "@portex-pro/ui-components";
import { FreightClass } from "api/graphql/generated";
import i18next from "i18next";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { freightClassDictionary } from "../../app/pages/shipments/components/EditableShipmentPackageGroupElements";
import { getFreightClass } from "./utils/getFreightClass";

const CUBIC_FEET_CONVERSION = 1728; // 12 * 12 * 12, to convert cubic inches to cubic feet

const formSchema = z.object({
  weight: z
    .number({ invalid_type_error: i18next.t("shipments:freightClassCalculator.validation.weightRequired") })
    .min(0.001, i18next.t("shipments:freightClassCalculator.validation.weightMin")),
  length: z
    .number({ invalid_type_error: i18next.t("freightClassCalculator.validation.lengthRequired") })
    .min(0.001, i18next.t("freightClassCalculator.validation.lengthMin")),
  width: z
    .number({ invalid_type_error: i18next.t("freightClassCalculator.validation.widthRequired") })
    .min(0.001, i18next.t("freightClassCalculator.validation.widthMin")),
  height: z
    .number({ invalid_type_error: i18next.t("freightClassCalculator.validation.heightRequired") })
    .min(0.001, i18next.t("freightClassCalculator.validation.heightMin")),
});

type FormValues = z.infer<typeof formSchema>;

type FreightClassCalculatorProps = {
  defaultValues?: Partial<FormValues>;
  onConfirm?: (value: FreightClass) => void;
};

const FreightClassCalculator: React.FC<FreightClassCalculatorProps> = ({
  defaultValues = {
    weight: 0,
    length: 0,
    width: 0,
    height: 0,
  },
  onConfirm,
}) => {
  const { t } = useTranslation("shipments");
  const [open, setOpen] = React.useState(false);
  const [result, setResult] = React.useState<{ density: number; freightClass: FreightClass } | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues,
  });

  const onSubmit = (data: FormValues) => {
    const { weight, length, width, height } = data;
    const cubicFeet = (length * width * height) / CUBIC_FEET_CONVERSION;

    if (cubicFeet === 0) {
      return;
    }

    const density = weight / cubicFeet;
    const freightClass = getFreightClass(density);

    setResult({ density: Math.round(density * 100) / 100, freightClass });
  };

  const onConfirmClick = () => {
    if (result?.freightClass && onConfirm) {
      onConfirm(result.freightClass);
    }
    setOpen(false);
  };

  const handleReset = () => {
    reset({
      weight: 0,
      length: 0,
      width: 0,
      height: 0,
    });
    setResult(null);
  };

  React.useEffect(() => {
    if (open) {
      reset(defaultValues);
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultValues]);

  return (
    <>
      <Link style={{ cursor: "pointer" }} onClick={() => setOpen(true)}>
        {t("freightClassCalculator.estimateLinkButton")}
      </Link>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <IconButton
          aria-label="close"
          onClick={() => setOpen(false)}
          style={{ position: "absolute", right: 12, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>{t("freightClassCalculator.title")}</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="weight"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("freightClassCalculator.weight")}
                      type="number"
                      fullWidth
                      margin="normal"
                      required
                      error={!!errors.weight}
                      helperText={errors.weight?.message}
                      onChange={(e) => field.onChange(e.target.value === "" ? "" : +e.target.value)}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="length"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("freightClassCalculator.length")}
                      type="number"
                      fullWidth
                      margin="normal"
                      required
                      error={!!errors.length}
                      helperText={errors.length?.message}
                      onChange={(e) => field.onChange(e.target.value === "" ? "" : +e.target.value)}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="width"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("freightClassCalculator.width")}
                      type="number"
                      fullWidth
                      margin="normal"
                      required
                      error={!!errors.width}
                      helperText={errors.width?.message}
                      onChange={(e) => field.onChange(e.target.value === "" ? "" : +e.target.value)}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="height"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t("freightClassCalculator.height")}
                      type="number"
                      fullWidth
                      margin="normal"
                      required
                      error={!!errors.height}
                      helperText={errors.height?.message}
                      onChange={(e) => field.onChange(e.target.value === "" ? "" : +e.target.value)}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {result && (
              <>
                <Typography variant="subtitle1" style={{ marginTop: 16 }}>
                  {t("freightClassCalculator.density", { density: result.density })}
                </Typography>
                <Typography variant="subtitle1">
                  {t("freightClassCalculator.freightClass", {
                    freightClass: freightClassDictionary[result.freightClass],
                  })}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleReset} variant="outlined">
              {t("freightClassCalculator.resetButton")}
            </Button>
            {result?.freightClass ? (
              <Button color="primary" variant="contained" disabled={!isValid} onClick={onConfirmClick}>
                Use this Freight Class
              </Button>
            ) : (
              <Button type="submit" color="primary" variant="contained" disabled={!isValid}>
                {t("freightClassCalculator.calculateButton")}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default FreightClassCalculator;
